// The full X12 Product/Service ID Qualifier code list (list 235) — shared by
// every "which item-identifier scheme follows" position across the 850's
// PO1 (baseline item) loop, its SLN sub-line, and the 852's LIN (item
// identification) loop. Transcribed from the reference document
// "Product Service ID Qualifiers.txt" (X12 235); footnote reference numbers
// from that document are not carried over, but every code, label and
// description it provides is.
//
// Each entry's `label` is the short name (used for the coded value's inline
// display); `description`, when the reference document provides a fuller
// explanation beyond the label, carries that sentence too (see
// dictionary/types.ts's `CodeListEntry`).

import type {CodeListEntry} from './types';

export const PRODUCT_SERVICE_ID_QUALIFIER_CODES: CodeListEntry[] = [
  {code: '00', label: 'Credit Card'},
  {
    code: 'A1',
    label: 'Plant Equipment Number',
    description: 'Item identifier is a plant equipment number',
  },
  {
    code: 'A2',
    label: 'Department of Defense Identification Code (DoDIC)',
    description:
      'Qualifies a code that uniquely identifies a type of explosive or ammunition',
  },
  {
    code: 'A3',
    label: 'Locally Assigned Control Number',
    description:
      'Item identifier is a locally assigned control number used to catalog and manage material',
  },
  {
    code: 'A4',
    label: 'Subsistence Identification Number',
    description: 'Item identifier is a brand-name resale subsistence item',
  },
  {code: 'A5', label: 'Application State or Province'},
  {
    code: 'A6',
    label: 'Document Identification Code',
    description:
      'Item identifier subclassifies the intended use of the transaction',
  },
  {
    code: 'A7',
    label: 'Subline Item Number',
    description:
      'A further subdivision of a contract or exhibit line item that identifies a specific part of that contract or exhibit line item',
  },
  {
    code: 'A8',
    label: 'Exhibit Line Item Number',
    description:
      'The line item of a document referenced in and appended to a procurement instrument which establishes a delivery requirement',
  },
  {code: 'A9', label: 'Activity'},
  {
    code: 'AA',
    label:
      'Assembly Level Code, Vehicle Maintenance Reporting Standards (VMRS) 2000 Code Key 32',
    description:
      'Assembly within the vehicle system that contains the causal component',
  },
  {code: 'AB', label: 'Assembly'},
  {code: 'AC', label: 'Aggregation Code (Used to Consolidate Part Families)'},
  {
    code: 'AD',
    label: 'American Dental Association Codes',
    description:
      "This association's membership consists of U.S. dentists. It sets standards for the dental profession",
  },
  {
    code: 'AE',
    label: 'Serial Item and Contribution Identifier (Defined in ANSI 239.56)',
  },
  {code: 'AF', label: 'Front Axle Serial Number'},
  {
    code: 'AG',
    label: 'Age',
    description:
      'The period during which, after distillation and before bottling, distilled spirits have been stored in oak containers',
  },
  {code: 'AH', label: 'Billboards Commercial'},
  {code: 'AI', label: 'Alternate ISBN'},
  {code: 'AJ', label: 'Piggyback Commercial'},
  {code: 'AK', label: 'Refined Product Code'},
  {code: 'AL', label: 'Axle Serial Number'},
  {code: 'AM', label: 'Allocation Methodology Identification Code'},
  {code: 'AN', label: 'Asset Number'},
  {
    code: 'AO',
    label: 'Assemblage Identification Number',
    description:
      'A system generated number used in combination with the Build Directive Number for internal control of component control numbers being applied to a particular medical or industrial kit or assembly',
  },
  {
    code: 'AP',
    label:
      'Component Level Code, Vehicle Maintenance Reporting Standards (VMRS) 2000 Code Key 33',
    description: 'Casual component within the vehicle assembly',
  },
  {code: 'AQ', label: 'Appraisal Product Type'},
  {code: 'AR', label: 'ARINC Part Number'},
  {code: 'AS', label: 'Rear Axle Serial Number'},
  {code: 'AT', label: 'Assortability Code'},
  {code: 'AU', label: 'Appraisal Service'},
  {
    code: 'AV',
    label: 'Additional Product Identification Assigned by the Manufacturer',
    description:
      'EAN.UCC (EAN International .Uniform Code Council) Application Identifier 240',
  },
  {code: 'AW', label: 'Well Number'},
  {
    code: 'AX',
    label: 'Assembly Lot Number',
    description:
      "A serialization number identifying good's manufacturing location",
  },
  {
    code: 'AY',
    label:
      'System Level Code, Vehicle Maintenance Reporting Standards (VMRS) 2000 Code Key 31',
    description: 'System within the vehicle that contains the casual assembly',
  },
  {code: 'AZ', label: 'Axle Ratio'},
  {code: 'B1', label: 'Volume Set'},
  {code: 'B2', label: 'Library of Congress Code'},
  {code: 'B3', label: 'Edition Code'},
  {code: 'B4', label: 'Binding Code'},
  {code: 'B5', label: 'Title Code'},
  {code: 'B6', label: 'Author Code'},
  {code: 'B7', label: 'Publisher Code'},
  {code: 'B8', label: 'Batch Number'},
  {code: 'B9', label: 'Front Axle Driven Serial Number'},
  {code: 'BA', label: 'Bale Number'},
  {
    code: 'BB',
    label: 'Print Color Standard Reference',
    description:
      'Customer decides after trial run of a print on a pre-dyed fabric a point of reference for the shade of the print color, light or darkness of the print; this reference then becomes a standard for the finisher to go by – as a customer orders fabric this code is used to identify to finisher the shade of the print on the fabric',
  },
  {code: 'BC', label: 'Supplier Brand Code'},
  {
    code: 'BD',
    label: 'Buyer Ground Shade Identifier',
    description:
      'For each color there are various degrees of the color red for instance has degrees chinese red, fire engine red, etc; to each degree a symbol called ground shade is established by both the buyer and seller; to the buyer it might be fire engine red, whereas to the seller the same classification might have a different symbol; need to identify both in a purchase order',
  },
  {code: 'BE', label: "Buyer's Engineering Change Level Number"},
  {
    code: 'BF',
    label: 'Vendor Ground Shade Identifier',
    description:
      'For each color there are various degrees of the color red for instance has degrees chinese red, fire engine red, etc; to each degree a symbol called ground shade is established by both the buyer and seller; to the buyer it might be fire engine red, whereas to the seller the same classification might have a different symbol; need to identify both in a purchase order',
  },
  {
    code: 'BG',
    label: 'Ground Shade Standard Reference',
    description:
      'Buyer established shade reference at time first lot was dyed; this refers seller (dyer) back to a previous lot to match the shade',
  },
  {
    code: 'BH',
    label: 'Finish/Hand Standard Reference',
    description:
      'Fabrics can be finished in many types of finish: water repellent, Scotch Guard, etc.; within each finish a mill can create a degree of softness or hardness depending on how fabric should feel to the customer; this degree is assigned a reference number for reordering purposes',
  },
  {code: 'BI', label: 'Rear Axle Rear Non Driven Serial Number'},
  {code: 'BJ', label: 'Application Completion Method'},
  {code: 'BK', label: 'Backing'},
  {code: 'BL', label: 'Brand/Label'},
  {code: 'BM', label: 'Beam Number'},
  {code: 'BN', label: 'Bar-Coded Serial Number'},
  {code: 'BO', label: 'Buyers Color'},
  {code: 'BP', label: "Buyer's Part Number"},
  {code: 'BQ', label: 'Benefit ID'},
  {code: 'BR', label: 'Compensation Type Code'},
  {code: 'BS', label: 'Bill Subgroup Code'},
  {code: 'BT', label: 'Volume Type Identification Code'},
  {code: 'BU', label: 'Bus Body Serial Number'},
  {code: 'BV', label: 'Beverage Common Code'},
  {code: 'BW', label: 'Compensation Allocation Code'},
  {code: 'BX', label: 'Bill Credit Code'},
  {code: 'BY', label: 'Concept Code'},
  {code: 'BZ', label: 'Coverage Type'},
  {code: 'C1', label: 'Channel'},
  {code: 'C2', label: 'Connector Type'},
  {code: 'C3', label: 'Classification'},
  {code: 'C4', label: 'Configuration Item Identifier'},
  {code: 'C5', label: 'Insurance Plan Description Characteristics'},
  {code: 'C6', label: 'Asset Type'},
  {
    code: 'C7',
    label: 'Asset Category',
    description: 'A description of the scope of investment assets',
  },
  {code: 'C8', label: 'Fund Sub-Advisor'},
  {code: 'C9', label: 'Dun & Bradstreet Standard Product and Service Code'},
  {code: 'CA', label: 'Case'},
  {code: 'CB', label: "Buyer's Catalog Number"},
  {code: 'CC', label: 'Compatible Cut Number'},
  {code: 'CD', label: 'Motor Vehicle-Line Designator'},
  {code: 'CE', label: 'Class of Contract Code'},
  {code: 'CF', label: 'Chassis Serial Number'},
  {code: 'CG', label: 'Commodity Grouping'},
  {code: 'CH', label: 'Country of Origin Code'},
  {code: 'CI', label: 'Common Language Equipment Identifier (CLEI)'},
  {
    code: 'CJ',
    label: 'Current Procedural Terminology (CPT) Codes',
    description:
      'Published by the AMA. It is a listing of descriptive terms and identifying codes for reporting medical services and procedures performed by physicians; the uniform language accurately designates medical, surgical, and diagnostic services, and thereby provides reliable communications among physicians, patients, and payers',
  },
  {code: 'CK', label: 'Candidate Oil'},
  {code: 'CL', label: 'Color'},
  {code: 'CM', label: 'National Retail Federation Color Code'},
  {code: 'CN', label: 'Commodity Name'},
  {code: 'CO', label: 'Chemical Abstract Service (CAS) Registry Number'},
  {code: 'CP', label: 'Carry-over Part Number'},
  {code: 'CQ', label: 'Equipment Code per COPAS standard'},
  {code: 'CR', label: 'Contract Number'},
  {code: 'CS', label: 'Service code per COPAS standard'},
  {
    code: 'CT',
    label: 'Continuation (ID Number Spans Multiple Product ID Data Elements)',
  },
  {code: 'CU', label: 'Cut Number'},
  {
    code: 'CV',
    label: 'Customer Provided Equipment',
    description:
      'The termination equipment being used will be provided by the customer',
  },
  {code: 'CW', label: 'Contract Activity Code'},
  {code: 'CX', label: 'Completion Number'},
  {code: 'CY', label: 'Customer Company Registry Number'},
  {
    code: 'CZ',
    label: 'Country from which Procured',
    description: 'Country from which the item was obtained',
  },
  {code: 'D1', label: 'Underwriting Method of Direct Writer'},
  {code: 'D2', label: 'Medical Information Bureau (MIB) Authorization'},
  {code: 'D3', label: 'Policy Form'},
  {code: 'D4', label: 'Plan Code'},
  {code: 'D5', label: 'Coverage Risk Type'},
  {code: 'D6', label: 'Medical Stop Loss Level Codes'},
  {code: 'D7', label: 'Medical Stop Loss Coverage Codes'},
  {code: 'D8', label: 'Medical Stop Loss Product Line Codes'},
  {code: 'D9', label: 'RxNorm'},
  {code: 'DA', label: 'Automobile Repair Product Code'},
  {code: 'DB', label: 'GS1 DataBar'},
  {
    code: 'DC',
    label:
      'International Classification of Diseases, 10th Revision, Clinical Modification (ICD-10-CM)',
    description:
      'The International Classification of Diseases, 10th Revision, Clinical Modification (ICD-10-CM), describes the classification of morbidity and mortality information for statistical purposes and for the indexing of healthcare records by diseases. This is a diagnosis code.',
  },
  {code: 'DD', label: 'Distributor'},
  {code: 'DE', label: 'Design Number'},
  {code: 'DF', label: 'Device Family'},
  {code: 'DG', label: 'Discount Grouping'},
  {code: 'DH', label: 'Transport4 Commodity Code'},
  {code: 'DI', label: 'Deposit Item Number'},
  {code: 'DL', label: 'Dye Lot Number'},
  {
    code: 'DM',
    label:
      'Committee for Uniform Security Identification Procedure Number (CUSIP) Number',
  },
  {code: 'DN', label: 'Die Number'},
  {code: 'DO', label: 'Dividend Use'},
  {code: 'DP', label: 'Discontinued Part Number'},
  {code: 'DQ', label: 'Event Type'},
  {code: 'DR', label: 'Drawing Revision Number'},
  {code: 'DS', label: 'Group ID'},
  {code: 'DT', label: 'Device Type'},
  {code: 'DU', label: 'Joint Life Type'},
  {code: 'DV', label: 'Location Code'},
  {code: 'DW', label: 'Nonforfeiture Option'},
  {
    code: 'DX',
    label:
      'International Classification of Diseases, 9th Revision, Clinical Modification (ICD-9-CM) – Diagnosis',
    description:
      'The International Classification of Diseases, 9th Revision, Clinical Modification (ICD-9-CM), Volumes I, II (diagnoses) and III (procedures), describes the classification of morbidity and mortality information for statistical purposes and for the indexing of healthcare records by diseases and procedures; this is a diagnosis code',
  },
  {code: 'DY', label: 'Premium Rate Type'},
  {code: 'DZ', label: 'Diagnosis Code Pointer'},
  {code: 'E1', label: 'Contract Change Authorization Type'},
  {code: 'E2', label: 'Fund Abbreviation'},
  {code: 'E3', label: 'Fund Type'},
  {code: 'E4', label: 'Related Policy Identification'},
  {code: 'E5', label: 'Tax Code'},
  {code: 'E6', label: 'Contract Transfer Reason Code'},
  {code: 'E7', label: 'Berenson-Eggers Type of Service'},
  {code: 'EA', label: 'EAN-99 In-store Coupon Code'},
  {code: 'EB', label: 'Fuel Tank Serial Number'},
  {code: 'EC', label: 'Engineering Change Level'},
  {code: 'ED', label: 'Engine Displacement Identification'},
  {code: 'EE', label: 'Premium Use'},
  {
    code: 'EF',
    label: 'Exhibit Identifier',
    description:
      'Identification letter for an exhibit attached to a solicitation.',
  },
  {code: 'EG', label: 'Purpose of Insurance'},
  {code: 'EH', label: 'Sales Presentation ID'},
  {code: 'EI', label: 'Expense Identifier'},
  {code: 'EJ', label: 'Service Feature ID'},
  {code: 'EK', label: 'Settlement/Payout Option'},
  {
    code: 'EL',
    label: "Buyer's Subline Item Number",
    description:
      "A subset of a buyer's item number (e.g., the relationship of a contract subline item number to its corresponding contract line item number",
  },
  {
    code: 'EM',
    label: 'Equipment Identification Number',
    description: 'Product identification number',
  },
  {
    code: 'EN',
    label: 'GTIN-13',
    description:
      'A 13-digit GS1 Identification Key composed of a GS1 Company Prefix, Item Reference and Check Digit used to identify trade items',
  },
  {
    code: 'EO',
    label: 'GTIN-8',
    description:
      'An 8-digit GS1 Identification Key composed of a GS1-8 Prefix, Item Reference, and Check Digit used to identify trade items',
  },
  {code: 'EP', label: "Buyer's End Product Number"},
  {code: 'EQ', label: 'Equipment Type'},
  {code: 'ER', label: 'Jurisdiction Specific Procedure and Supply Codes'},
  {code: 'ES', label: 'Engine Serial Number'},
  {
    code: 'ET',
    label: 'Department of Defense Enterprise Identifier',
    description:
      'An activity identifier code assigned to the entity that is responsible for assigning a unique item identifier to an item',
  },
  {code: 'EU', label: 'Skill Code'},
  {code: 'EX', label: 'Exchanged Part, Assembly or Product'},
  {code: 'EZ', label: 'Shift Worked'},
  {
    code: 'F1',
    label: 'Catalog Number',
    description:
      "Item identifier specifies a manufacturer's catalog that applies to an item",
  },
  {
    code: 'F2',
    label: 'Technical Order Number',
    description:
      'Item identifier specifies a technical order that applies to an item',
  },
  {
    code: 'F3',
    label: 'Technical Manual Number',
    description:
      'Item identifier specifies the technical manual listing the item',
  },
  {
    code: 'F4',
    label: 'Series Identifier',
    description:
      'An identification of equipment having the same basic design but not necessarily identical configurations (within the equipment model)',
  },
  {
    code: 'F5',
    label: 'Obligation Authority Number',
    description:
      'Item identifier specifies the funding authority related to an expense or procurement',
  },
  {code: 'F6', label: 'First Prior Identifier'},
  {
    code: 'F7',
    label: 'End-Item Description',
    description:
      'Item identifier describes an end-item associated with the use of the required material',
  },
  {
    code: 'F8',
    label: 'Next Higher Used Assembly',
    description:
      'Item identifier describes the next higher assembly associated with the use of the required material',
  },
  {code: 'F9', label: 'Former Publisher'},
  {code: 'FA', label: 'Failed Subassembly Serial Number'},
  {code: 'FB', label: 'Form Number'},
  {code: 'FC', label: 'Coupon Family Code'},
  {code: 'FD', label: 'Fund'},
  {code: 'FE', label: 'Feature'},
  {code: 'FF', label: 'Fifth Wheel Serial Number'},
  {code: 'FG', label: 'Fund Manager'},
  {code: 'FH', label: 'Freddie Mac Affordable Lending Product Code'},
  {code: 'FI', label: 'Finish Number'},
  {code: 'FJ', label: 'Fannie Mae Affordable Lending Product Code'},
  {code: 'FK', label: 'Freddie Mac Project Condominium Classification Code'},
  {code: 'FL', label: 'Finish Lot Number'},
  {code: 'FM', label: 'Failed Subassembly Model Number'},
  {
    code: 'FN',
    label: 'Final Test Lot Number',
    description:
      'A serialization number identifying the final test event of goods',
  },
  {code: 'FP', label: 'Fabric Pieces Per Roll'},
  {code: 'FQ', label: 'Fannie Mae Project Condominium Classification Code'},
  {code: 'FR', label: 'Front Axle, Rear'},
  {code: 'FS', label: 'National Stock Number'},
  {code: 'FT', label: 'Federal Supply Classification'},
  {
    code: 'FU',
    label: 'National Alcohol Beverage Control Association (NABCA) Product Code',
  },
  {code: 'FV', label: 'Drug Identification Number (DIN)'},
  {
    code: 'FW',
    label: 'New Microcode',
    description:
      'Microcode: 1) One or more microinstructions, 2) A code, representing the instructions of an instruction set, that is implemented in a part of storage that is not program-addressable',
  },
  {code: 'FX', label: 'Federal Supply Group'},
  {code: 'GA', label: 'Gathering'},
  {code: 'GC', label: 'Grade Code'},
  {code: 'GD', label: 'Grain Direction'},
  {code: 'GE', label: 'Generic Name Description'},
  {code: 'GI', label: 'Graphics Industry Bar Code (GIBC)'},
  {
    code: 'GK',
    label: 'Glider Kit',
    description:
      'In the automotive industry a "glider kit" is the term for an incomplete vehicle with a vehicle identification number assigned and is typically manufactured without an engine, transmission, driveline, or rear axle(s); often the glider kit is used as the replacement shell for a vehicle involved in an accident',
  },
  {
    code: 'GM',
    label: 'General Services Administration (GSA) Special Item Number',
  },
  {code: 'GN', label: 'Grade Name'},
  {code: 'GQ', label: 'Group Qualifier Code'},
  {code: 'GR', label: 'Gear Ratio'},
  {code: 'GS', label: 'General Specification Number'},
  {code: 'GU', label: 'Volume Usage Identification Code'},
  {
    code: 'GV',
    label: 'Serialized Global Returnable Asset Identifier (GRAI)',
    description:
      'The globally unique GS1 System identification number for a reusable package or transport equipment of a certain value, such as a beer keg, gas cylinder or pallet. The identifier includes a 1 to 16 character serial number. The serialized GRAI enables returnable assets to be identified by type or individually for tracking or sorting purposes',
  },
  {
    code: 'GW',
    label: 'Global Returnable Asset Identifier (GRAI)',
    description:
      'An identifier that is used to identify a returnable asset represented as a reusable package or transport equipment. The GRAI makes use of the GS1 Company Prefix to generate the unique asset number',
  },
  {
    code: 'GX',
    label: 'Global Individual Asset Identifier (GIAI)',
    description:
      'An identifier that is used to uniquely identify an entity that is part of the fixed inventory of a company. The GIAI makes use of the GS1 Company Prefix to generate the unique asset number',
  },
  {
    code: 'HA',
    label: 'Country Subdivision Code',
    description: 'ISO 3166-2 code value for state or province',
  },
  {
    code: 'HB',
    label: 'Country of Origin with Country Subdivision Code',
    description:
      'ISO 3166-1 country code value and ISO 3166-2 state or province code value',
  },
  {
    code: 'HC',
    label: 'Healthcare Common Procedure Coding System (HCPCS) Codes',
    description:
      'Centers for Medicare & Medicaid Services (CMS) coding scheme to group procedure(s) performed on an outpatient basis for payment to hospital under Medicare; primarily used for ambulatory surgical and other diagnostic departments',
  },
  {
    code: 'HD',
    label: 'International Harmonized Commodity Code',
    description:
      'For customs authority purposes, the class and description of a subject part or material item per harmonized agreements on tariff commodity codes',
  },
  {
    code: 'HI',
    label:
      'HIBC (Health Care Industry Bar Code) Supplier Labeling Standard Primary Data Message',
    description:
      'The primary data message consists of the LIC (Labeler Identification Code), product catalog number, and unit-of-measure identifier',
  },
  {code: 'HN', label: 'Heat Number'},
  {
    code: 'HP',
    label: 'Health Insurance Prospective Payment System (HIPPS) Rate Code',
  },
  {code: 'IA', label: 'Information Media Type'},
  {code: 'IB', label: 'International Standard Book Number (ISBN)'},
  {code: 'IC', label: 'Interior Color Number'},
  {
    code: 'ID',
    label:
      'International Classification of Diseases, 9th Revision, Clinical Modification (ICD-9-CM) – Procedure',
    description:
      'The International Classification of Diseases, 9th Revision, Clinical Modification (ICD-9-CM), Volumes I, II (diagnoses) and III (procedures), describes the classification of morbidity and mortality information for statistical purposes and for the indexing of healthcare records by diseases and procedures; this is a procedure code',
  },
  {code: 'IE', label: "Insurer's Fund Code"},
  {code: 'IF', label: 'Investment Fund Type'},
  {code: 'IG', label: 'Ignition Key Number'},
  {
    code: 'IH',
    label:
      'International Classification of Diseases, 11th Revision, Clinical Modification (ICD-11-CM)',
  },
  {code: 'II', label: 'Commodity Item Identification'},
  {
    code: 'IJ',
    label:
      'International Classification of Diseases, 11th Revision, Procedure Coding System (ICD-11-PCS)',
  },
  {code: 'IM', label: 'Imprint (Trademark Code of Subsidiary)'},
  {code: 'IN', label: "Buyer's Item Number"},
  {
    code: 'IP',
    label:
      'International Classification of Diseases, 10th Revision, Procedure Coding System (ICD-10-PCS)',
    description:
      'The International Classification of Diseases, 10th Revision, Procedure Coding System (ICD-10-PCS), describes the classification of inpatient procedures for statistical purposes and for the indexing of healthcare records by procedures. This is a procedure code.',
  },
  {code: 'IQ', label: 'IRS Qualification Code'},
  {code: 'IR', label: 'Ingredient'},
  {code: 'IS', label: 'International Standard Serial Number (ISSN)'},
  {code: 'IT', label: "Buyer's Style Number"},
  {
    code: 'IU',
    label: 'Department of Defense Issuing Agency Code',
    description:
      'A designator that represents the registration authority that issued the enterprise identifier',
  },
  {
    code: 'IW',
    label: 'Interchangeability Code',
    description:
      'Air Transport Association code specifying the interchangeability level among components',
  },
  {code: 'IZ', label: "Buyer's Size Code"},
  {code: 'JA', label: 'Anniversary'},
  {code: 'JB', label: 'Commission Identifier'},
  {code: 'JC', label: 'Commission Year'},
  {code: 'JD', label: 'Contribution Year'},
  {code: 'JN', label: 'Job Number'},
  {
    code: 'JP',
    label: 'Package Type Code',
    description: 'Packaging method used for manufacturer product',
  },
  {code: 'JS', label: 'Job Sequence Number'},
  {code: 'KA', label: 'Engineering Data List'},
  {code: 'KB', label: 'Data Category Code'},
  {code: 'KD', label: 'Replacement National Stock Number'},
  {code: 'KE', label: 'Military Standard'},
  {code: 'KF', label: 'Item Type Number'},
  {code: 'KG', label: 'Time Compliant Technical Order'},
  {code: 'KI', label: 'Cognizance Symbol'},
  {code: 'KJ', label: 'Material Control Code'},
  {code: 'KK', label: 'Special Material Identification Code'},
  {code: 'KL', label: 'Item Management Code'},
  {code: 'KM', label: 'Shelf-Life Code'},
  {code: 'KN', label: 'Shelf-Life Action Code'},
  {code: 'KP', label: 'Kanban Plan Number'},
  {code: 'L1', label: 'Program Level'},
  {code: 'L2', label: 'Topic Level'},
  {code: 'L3', label: 'Subtopic Level'},
  {code: 'L4', label: 'Life/Annuity Service Features'},
  {code: 'L5', label: 'Line of Authority'},
  {code: 'L6', label: 'Lube, Synthetic'},
  {code: 'LA', label: 'Labor Group'},
  {
    code: 'LB',
    label: 'Logical Observation Identifier Names and Codes (LOINC) Codes',
  },
  {code: 'LC', label: 'Laboratory Test Condition Code'},
  {code: 'LD', label: 'SNOMED, Systematized Nomenclature of Medicine'},
  {code: 'LG', label: 'Lift Gate Serial Number'},
  {code: 'LM', label: 'Lottery Game Number'},
  {code: 'LN', label: 'Lottery Pack/Book Number'},
  {code: 'LP', label: 'Life/Annuity Product Code'},
  {code: 'LR', label: 'Lease Number'},
  {code: 'LS', label: 'Load Sequence'},
  {code: 'LT', label: 'Lot Number'},
  {code: 'LU', label: 'Lot Pricing Unit Number'},
  {code: 'MA', label: 'Machine Number'},
  {code: 'MB', label: 'Measurement Type Code'},
  {code: 'MC', label: 'Mortgage Credit Data Order Type'},
  {code: 'MD', label: 'Method of Delivery Code'},
  {code: 'ME', label: 'Market Program Code'},
  {code: 'MF', label: 'Manufacturer'},
  {code: 'MG', label: "Manufacturer's Part Number"},
  {code: 'MH', label: 'Medication Code'},
  {code: 'MI', label: 'Mortgage Insurance Product Code or Number'},
  {code: 'MJ', label: 'Manual Transmission Serial Number'},
  {code: 'MK', label: 'Front Axle Non Driven Serial Number'},
  {
    code: 'MM',
    label: 'Motor Equipment Manufacturing Association (MEMA) Product Type Code',
  },
  {code: 'MN', label: 'Model Number'},
  {code: 'MO', label: 'Movement Type Code'},
  {code: 'MP', label: 'Mortgage Product Code'},
  {code: 'MQ', label: 'Mortgage Underwriting Type'},
  {code: 'MR', label: 'Maintenance Index Page Reference Number'},
  {code: 'MS', label: 'Military Specification (MILSPEC) Number'},
  {code: 'MT', label: 'Major Product/Material/Machine Type'},
  {code: 'MU', label: 'Authorized Parts List Number'},
  {code: 'MV', label: 'Equipment Location'},
  {code: 'MW', label: 'Equipment Hierarchical Sequence Identifier'},
  {code: 'MX', label: 'Repair Induction Identifier'},
  {
    code: 'N1',
    label: 'National Drug Code in 4-4-2 Format',
    description:
      '4-digit manufacturer ID, 4-digit product ID, 2-digit trade package size',
  },
  {
    code: 'N2',
    label: 'National Drug Code in 5-3-2 Format',
    description:
      '5-digit manufacturer ID, 3-digit product ID, 2-digit trade package size',
  },
  {
    code: 'N3',
    label: 'National Drug Code in 5-4-1 Format',
    description:
      '5-digit manufacturer ID, 4-digit product ID, 1-digit trade package size',
  },
  {
    code: 'N4',
    label: 'National Drug Code in 5-4-2 Format',
    description:
      '5-digit manufacturer ID, 4-digit product ID, 2-digit trade package size',
  },
  {code: 'N5', label: 'National Health Related Item Code in 5-5 Format'},
  {code: 'N6', label: 'National Health Related Item Code in 4-6 Format'},
  {code: 'ND', label: 'National Drug Code (NDC)'},
  {
    code: 'NE',
    label: 'Yarn Count – English',
    description:
      'Identifies that the spun yarn number is expressed in English units',
  },
  {code: 'NG', label: 'National Glass Association (NAGS) Number'},
  {code: 'NH', label: 'National Health Related Item Code'},
  {
    code: 'NM',
    label: 'Yarn Count – Metric',
    description:
      'Identifies that the spun yarn number is expressed in Metric units',
  },
  {
    code: 'NN',
    label: 'National Item Identification Number',
    description:
      'A number assigned to each approved item identification under the Federal Cataloging Program',
  },
  {code: 'NP', label: 'Natural Health Product Number'},
  {code: 'NR', label: 'Non-resaleable item (excluding deposit) number'},
  {code: 'NU', label: 'National Uniform Billing Committee (NUBC) UB92 Codes'},
  {code: 'NW', label: 'New Replacement Part or Assembly Defective'},
  {code: 'NZ', label: 'Combined NCCMA/Bank Service Code'},
  {
    code: 'O9',
    label: "Old Vendor's (Seller's) Item Number",
    description:
      'Code used to identify the old identifier used in the life cycle of an item',
  },
  {code: 'OE', label: 'Original Equipment Number'},
  {
    code: 'OF',
    label: 'Old Common Language Equipment Identifier (CLEI) Code',
    description:
      'Old Common Language Equipment Identifier (CLEI) codes are distinctive codes that provide a standard method of identifying telecommunications equipment or products in a uniform feature-oriented language',
  },
  {
    code: 'OG',
    label: 'Old Microcode',
    description:
      'Microcode: 1) One or more microinstructions, 2) A code, representing the instructions of an instruction set, that is implemented in a part of storage that is not program-addressable',
  },
  {code: 'OH', label: 'Opposite-Hand Part Number'},
  {code: 'OI', label: 'Optical Industry Product Code'},
  {code: 'OL', label: 'Optical Cable Code'},
  {
    code: 'OM',
    label: 'Original Part Number',
    description:
      'A number assigned to all instances of a class of items that have the same form, fit, function, and interface at the time of their creation by the enterprise responsible for the manufacture, production, or creation of the items',
  },
  {code: 'ON', label: 'Customer Order Number'},
  {
    code: 'OO',
    label: 'Outside Production Operation Sheet Number',
    description:
      'The specific engineering level or release to be used for the manufacture of the material being procured',
  },
  {
    code: 'OP',
    label: 'Obsolete Part Number',
    description:
      'A number assigned to all instances of a class of items that have the same form, fit, function, and interface at the time of their creation by the enterprise responsible for the manufacture, production, or creation of the items',
  },
  {code: 'OR', label: 'Offer Number'},
  {code: 'OT', label: 'Internal Number'},
  {code: 'OU', label: 'Original Unit of Issue'},
  {
    code: 'P1',
    label:
      'Petroleum Accountants Society of Canada Operating and Maintenance Code – Goods and Services Tax Not Applicable',
  },
  {
    code: 'P2',
    label:
      'Petroleum Accountants Society of Canada Capital Expenditure Code – Goods and Services Tax Not Applicable',
  },
  {code: 'P3', label: 'Petroleum Accountants Society of Canada Tubular Code'},
  {
    code: 'P4',
    label: 'Petroleum Accountants Society of Canada Non-Tubular Code',
  },
  {
    code: 'P5',
    label: 'Material Discharge Number',
    description: 'Number identifying the point where material is discharged',
  },
  {code: 'P6', label: 'Pump, Fire'},
  {code: 'P7', label: 'Previous Carrier'},
  {code: 'P8', label: 'Retail Price Look Up Number (PLU)'},
  {
    code: 'P9',
    label: 'Ply',
    description:
      'The number of single yarns twisted together to form a plied yarn',
  },
  {code: 'PA', label: 'Pattern Number'},
  {
    code: 'PB',
    label:
      'Petroleum Accountants Society of Canada Operating and Maintenance Code – Goods and Services Tax Forwarded',
  },
  {code: 'PC', label: 'Prime Contractor Part Number'},
  {code: 'PD', label: 'Part Number Description'},
  {code: 'PE', label: 'Pieces in Roll'},
  {
    code: 'PF',
    label:
      'Petroleum Accountants Society of Canada Capital Expenditure Code – Goods and Services Tax Forwarded',
  },
  {code: 'PG', label: 'Packaging Specification Number'},
  {code: 'PH', label: 'Property and Casualty Service Code'},
  {code: 'PI', label: "Purchaser's Item Code"},
  {
    code: 'PJ',
    label:
      'Product Date Code (A code indicating the period during which a product was manufactured.)',
  },
  {code: 'PK', label: 'Packaging Drawing'},
  {code: 'PL', label: "Purchaser's Order Line Number"},
  {code: 'PM', label: 'Number of Positions on Machine'},
  {code: 'PN', label: 'Company Part Number'},
  {code: 'PO', label: 'Purchase Order Number'},
  {
    code: 'PP',
    label: 'Air Transportation Association Proprietary Rights Code',
    description:
      'Primary procurement source for the subject part number and the relationship of proprietary rights to the originator of the data',
  },
  {code: 'PQ', label: 'Product ID Attribute Code'},
  {code: 'PR', label: 'Process Number'},
  {code: 'PS', label: 'Position'},
  {code: 'PT', label: 'Print or Drawing'},
  {
    code: 'PU',
    label: 'Part Reference Number',
    description:
      'Often on a part or subassembly drawing there is a reference number shown that is not a part number but may refer to a set of notes for use in fabrication, assembly, or in repairing the item; it may refer to a physical location within the product, such as location of a particular part on a truck engine, or to a reference to a circuit location on an electronic assembly',
  },
  {code: 'PV', label: 'Advertising Package Identification Code'},
  {code: 'PW', label: 'Part Drawing'},
  {
    code: 'PX',
    label: 'Secondary Ply',
    description:
      'The number of plied yarns twisted together to form a thick strand yarn, also known as cabling',
  },
  {
    code: 'PY',
    label: 'Operator Assigned Property Identification',
    description:
      'Number assigned by the operator to the physical property where the drilling is conducted; the number is used to identify the product or service for which the joint partner is being billed',
  },
  {code: 'PZ', label: 'Product Change Notice Number'},
  {code: 'R1', label: 'Replacement Subassembly Model Number'},
  {code: 'R2', label: 'Replacement Subassembly Serial Number'},
  {code: 'R3', label: 'Rear Axle, Middle'},
  {code: 'R4', label: 'Rear Axle, Pusher'},
  {code: 'R5', label: 'Rear Axle, Tag'},
  {code: 'R6', label: 'Rear Axle, Extended Tag'},
  {
    code: 'R9',
    label: "Replacement Vendor's (Seller's) Item Number",
    description:
      'Code used to identify the replacement identifier in the life cycle of an item',
  },
  {code: 'RA', label: 'Return Code'},
  {code: 'RB', label: 'National Uniform Billing Committee (NUBC) UB82 Codes'},
  {code: 'RC', label: 'Returnable Container Number'},
  {code: 'RD', label: 'Reel Number'},
  {code: 'RE', label: 'Reefer Serial Number'},
  {
    code: 'RF',
    label: 'Repair From Product Code',
    description:
      'Identifies a broken product that is to be used in a repair process to create a new product with different attributes',
  },
  {code: 'RG', label: 'Reference Oil'},
  {code: 'RH', label: 'Radiator Serial Number'},
  {code: 'RI', label: 'Rear Axle Front Rear Driven Serial Number'},
  {code: 'RJ', label: 'Rear Axle Rear Driven Serial Number'},
  {code: 'RK', label: 'Rack Number'},
  {code: 'RL', label: 'Rate Detail Card'},
  {code: 'RM', label: 'Related Model Number'},
  {code: 'RN', label: 'Release Number'},
  {code: 'RO', label: 'Roll Number'},
  {code: 'RP', label: 'Replaced Part Number'},
  {code: 'RQ', label: 'Automobile Rental Charge Item Code'},
  {code: 'RR', label: 'Replacement Product Number'},
  {code: 'RS', label: 'Set Number'},
  {code: 'RT', label: 'Reel Type'},
  {code: 'RU', label: 'Run Number'},
  {code: 'RV', label: 'Repair Tag Number'},
  {code: 'RW', label: 'Relative Value Units'},
  {code: 'RX', label: 'Provisioning Reference Number'},
  {code: 'RY', label: 'Record Keeping or Model Year'},
  {code: 'RZ', label: 'Related Model Type'},
  {code: 'S1', label: "Shipper's Item Number"},
  {code: 'S2', label: 'Second Prior Identifier'},
  {code: 'S3', label: 'Phase'},
  {code: 'S4', label: 'Laboratory Sample Identification'},
  {code: 'S5', label: 'State Sample Identification'},
  {code: 'S6', label: 'Previous Sample Identification'},
  {code: 'S7', label: 'Source of Deposit Code'},
  {code: 'S8', label: 'Source of Lead Code'},
  {code: 'SA', label: 'Schematic Diagram Reference Number'},
  {
    code: 'SB',
    label: 'Submission Number',
    description:
      'A sequential number indicating the number of times a lot has been submitted for a certain test event',
  },
  {code: 'SC', label: "Seller's Date Code"},
  {code: 'SD', label: 'Supplier Company Registry Number'},
  {code: 'SE', label: 'Section Print Number'},
  {code: 'SF', label: 'Surface Finish'},
  {code: 'SG', label: 'Seat Serial Number'},
  {
    code: 'SH',
    label: 'Service Requested',
    description:
      'A numeric or alphanumeric code from a list of services available to the customer',
  },
  {code: 'SI', label: 'Standard Industrial Classification Code'},
  {code: 'SJ', label: 'Religious Retail Non-book Item'},
  {code: 'SK', label: 'Stock Keeping Unit (SKU)'},
  {code: 'SL', label: "Seller's Lot Number"},
  {code: 'SM', label: 'National Retail Federation Size Code'},
  {code: 'SN', label: 'Serial Number'},
  {code: 'SO', label: 'System Identifier'},
  {
    code: 'SP',
    label: 'Superseded Purchase Order Number',
    description:
      'When moving material from one customer to another, the material is being taken off of the Superseded P.O. and put on a new purchase order number; this will allow tracking of the material to the original P.O. Number when the superseded purchase order number is used to specifically identify the item',
  },
  {code: 'SQ', label: 'Roll Sequence Number'},
  {code: 'SR', label: 'Substitute Product Number'},
  {code: 'SS', label: 'Superseded Part Number'},
  {code: 'ST', label: 'Style Number'},
  {code: 'SU', label: 'Side Up/Side Down'},
  {code: 'SV', label: 'Service Rendered'},
  {code: 'SW', label: 'Stock Number'},
  {code: 'SX', label: 'Sleeper Box Key Number'},
  {code: 'SY', label: 'Sleeper Box Serial Number'},
  {code: 'SZ', label: 'Vendor Alphanumeric Size Code'},
  {
    code: 'T2',
    label: 'Tex',
    description:
      'A system of numbering yarns of all types of fibers; extensively used outside the U.S. for classification of exports',
  },
  {code: 'T3', label: 'Third Prior Identifier'},
  {code: 'TA', label: 'Pipeline Transaction Code'},
  {
    code: 'TB',
    label:
      'Association for Financial Professionals Service Code and Bank Service Code',
    description:
      'Six-digit Association for Financial Professionals (AFP) Service Code and a proprietary code assigned by the vendor',
  },
  {code: 'TC', label: 'Telecommunications Circuit ID'},
  {code: 'TD', label: 'Treatment Codes'},
  {
    code: 'TE',
    label: 'Association for Financial Professionals Service Code',
    description:
      'Six-digit Association for Financial Professionals (AFP) Service Code',
  },
  {code: 'TF', label: 'The Air Cargo Tariff (TACT) Commodity Code'},
  {code: 'TG', label: 'Automatic Transmission Serial Number'},
  {code: 'TH', label: 'Transfer Case Serial Number'},
  {code: 'TI', label: 'Trade In Identifier'},
  {code: 'TJ', label: 'Auxiliary Transmission Serial Number'},
  {code: 'TK', label: 'Transmission, Auxillary'},
  {code: 'TM', label: 'Telephone Industry Manufacturer Code'},
  {
    code: 'TN',
    label: 'Railroad-Owned Unit Train Number',
    description:
      'Railroad Train Number composed of cars containing the same commodity with the same origin and destination',
  },
  {code: 'TP', label: 'Product Type Code'},
  {code: 'TR', label: 'Truck Body Serial Number'},
  {code: 'TS', label: 'Transmission Serial Number'},
  {code: 'TT', label: 'Type Selvage'},
  {code: 'TU', label: 'Tested Material Identification Number'},
  {code: 'TV', label: 'Line of Business'},
  {code: 'TW', label: 'Program Code'},
  {
    code: 'TX',
    label: 'Federal Aviation Administration (FAA) Service Bulletin Number',
    description:
      'The Federal Aviation Administration (FAA) Service Bulletin number which authorizes replacement of a subject part by the replacing part number',
  },
  {code: 'TY', label: 'Telecommunications Industry Service Code'},
  {code: 'TZ', label: 'Program Description Identifier'},
  {code: 'U3', label: 'United Nations Common Coding System (UNCCS)'},
  {code: 'U5', label: 'Broker Price Opinion Service'},
  {code: 'U6', label: 'Real Estate Property Information Service'},
  {
    code: 'U7',
    label: 'Department of Defense Unique Item Identifier',
    description: 'An identifier used to uniquely identify an individual item',
  },
  {code: 'UC', label: 'Product Variant'},
  {code: 'UF', label: 'User-Defined Shipping Container Identifier'},
  {code: 'UJ', label: 'U.P.C./EAN Coupon Code (2-5-5)'},
  {
    code: 'UK',
    label: 'GTIN-14',
    description:
      'A 14-digit GS1 Identification Key composed of an Indicator Digit, GS1 Company Prefix, Item Reference and Check Digit used to identify trade items',
  },
  {code: 'UL', label: 'U.P.C. Coupon Code (1-5-5-1)'},
  {code: 'UM', label: 'Universal Vendor Marking, Short Code (UVM; U Line)'},
  {
    code: 'UO',
    label: 'EAN.UCC Serial Shipping Container Code (SSCC)',
    description:
      "A 20 digit number comprising Application Identifier (AI) '00' and SSCC",
  },
  {
    code: 'UP',
    label: 'GTIN-12',
    description:
      'A 12-digit GS1 Identification Key composed of a GS1 Company Prefix, Item Reference, and Check Digit used to identify trade items',
  },
  {code: 'UQ', label: 'United Nations (UN) Number (Dangerous Goods)'},
  {code: 'UR', label: 'UCC/EAN-128 Coupon Extended Code'},
  {code: 'US', label: 'Uniform Stock Symbol System Code Number'},
  {
    code: 'UT',
    label: 'Company-Owned Unit Train Number',
    description:
      'Company Train Number composed of cars containing the same commodity with the same origin and destination',
  },
  {code: 'UV', label: 'Universal Vendor Marking, Long Code (UVM; R,P,M Lines)'},
  {
    code: 'UX',
    label: 'Universal Product Number',
    description: 'A unique identifier of a healthcare product',
  },
  {
    code: 'UY',
    label:
      'Department of Defense Unique Item Identifier Type Reference Identifier',
    description:
      'A designator that indicates the type of unique item identifier to uniquely identify an item',
  },
  {code: 'UZ', label: 'GS1 US Coupon Code with GS1-128 Coupon Extended Code'},
  {code: 'V1', label: 'Ingredient Country of Origin Code'},
  {code: 'V2', label: 'Tariff Country of Origin Code'},
  {code: 'V3', label: 'Country of Last Processing Code'},
  {code: 'V4', label: 'Country of Assembly Code'},
  {code: 'V5', label: 'Logistics Country of Origin Code'},
  {
    code: 'V6',
    label:
      'United States Department of Agriculture (USDA) Country of Origin Code',
    description:
      'Country of origin per United States Department of Agriculture regulations',
  },
  {code: 'VA', label: "Vendor's Style Number"},
  {code: 'VB', label: "Vendor's Engineering Change Level Number"},
  {code: 'VC', label: "Vendor's (Seller's) Catalog Number"},
  {code: 'VE', label: 'Vendor Color'},
  {code: 'VI', label: 'Vary Item Product Number'},
  {code: 'VM', label: 'Vehicle Maintenance Reporting Standards'},
  {code: 'VN', label: "Vendor's (Seller's) Item Number"},
  {code: 'VO', label: "Vendor's Order Number"},
  {code: 'VP', label: "Vendor's (Seller's) Part Number"},
  {code: 'VS', label: "Vendor's Supplemental Item Number"},
  {
    code: 'VT',
    label: 'Vintage',
    description:
      'The harvest year of the grapes that are part of the composition of a particular wine',
  },
  {code: 'VU', label: "Vendor's Basic Unit Number"},
  {code: 'VV', label: 'Motor Vehicle ID Number'},
  {code: 'VX', label: "Vendor's Specification Number"},
  {
    code: 'W1',
    label: 'End Item Serial Number',
    description: 'The serial number assigned to the entire end-item assembly',
  },
  {
    code: 'W2',
    label: 'Work Unit Number',
    description:
      'Number assigned for type of work or service identification associated with the product',
  },
  {
    code: 'W5',
    label: 'Reclamation Process',
    description:
      'Material must be reclaimed from a higher level system prior to release to Defense Reutilization and Marketing',
  },
  {
    code: 'W6',
    label: 'Woolen Run',
    description: 'U.S. system for numbering wool yarn using run process',
  },
  {
    code: 'W7',
    label: 'Woolen Cut',
    description: 'U.S. system for numbering wool yarn using cut process',
  },
  {code: 'WA', label: 'Random Weight Aggregation Code'},
  {code: 'WB', label: 'Car Class Code'},
  {code: 'WC', label: 'World Code'},
  {code: 'WD', label: 'Airline Flight Code'},
  {code: 'WE', label: 'Fare Basis Code'},
  {code: 'WF', label: 'Service Class Code'},
  {code: 'WG', label: 'Stop Over Code'},
  {code: 'WJ', label: 'Telecom Service Type'},
  {code: 'WK', label: 'Advanced Billing Concepts (ABC) Codes'},
  {
    code: 'WL',
    label: 'Wafer Lot Identifier',
    description:
      'A serialization number identifying the manufacturer of integrated circuit wafers',
  },
  {
    code: 'WR',
    label: 'Yarn Count Worsted',
    description: 'System for numbering spun yarn of long staple fibers',
  },
  {code: 'WS', label: 'Wheel Chair Lift Serial Number'},
  {code: 'XA', label: 'Preferred Part Number'},
  {code: 'XC', label: 'Expendable Container Identification'},
  {code: 'XN', label: 'Export Control Classification Number (ECCN)'},
  {code: 'XP', label: 'Preferred National Stock Number'},
  {code: 'XQ', label: 'Preferred Manufacturer'},
  {code: 'XZ', label: 'Contractor Establishment Code'},
  {
    code: 'YM',
    label: 'Map Edition Number',
    description:
      'A code identifying the edition number for the map being identified',
  },
  {code: 'YP', label: 'Publication Number'},
  {
    code: 'ZB',
    label: 'Commercial and Government Entity (CAGE) Code',
    description:
      'A code that identifies a commercial contractor authorized to do business with the U.S. government',
  },
  {code: 'ZR', label: 'Service Control Identification'},
  {code: 'ZZ', label: 'Mutually Defined'},
];
